/**
 * The console's shared charts (doc 03 §6.9): `TrendChart` and `FunnelBars`, beside `Sparkline` in
 * the kit. Screens hand them data and labels; nothing below knows which metric it draws.
 *
 * Hand-drawn SVG and CSS, not Recharts: two charts did not justify a charting dependency, and the
 * rules the doc sets hold either way. Colours are console tokens, every series is named by a
 * legend chip rather than by colour alone, and every value can be read exactly — the trend's
 * lines carry their daily numbers as a tooltip and the funnel prints each count beside its bar.
 * Nothing animates, so `prefers-reduced-motion` has nothing to turn off.
 */
import { Fragment, useState, type ReactNode } from 'react';
import { Icon } from './primitives';

export interface TrendSeries {
  key: string;
  label: string;
  /** A console token, e.g. `var(--c-info)`. */
  tone: string;
  /** One value per day, oldest first; every series has the same length. */
  points: readonly number[];
}

const fmt = (n: number) => n.toLocaleString('en-IN');

/**
 * Daily lines on one scale, with a legend chip per series that hides or shows it. The scale
 * follows what is shown, so hiding a large series lets the small ones fill the height.
 */
export function TrendChart({
  series,
  label = 'Daily',
  height = 180,
}: {
  series: readonly TrendSeries[];
  /** The chart's accessible name opens with this, then names the series shown. */
  label?: string;
  height?: number;
}) {
  const [hidden, setHidden] = useState<readonly string[]>([]);
  const shown = series.filter((s) => !hidden.includes(s.key));
  const max = Math.max(1, ...shown.flatMap((s) => s.points));
  const line = (points: readonly number[]) =>
    points.map((p, i) => `${(i / Math.max(1, points.length - 1)) * 100},${100 - (p / max) * 96}`).join(' ');

  return (
    <>
      <div style={{ display: 'flex', gap: 'var(--sp-xs)', flexWrap: 'wrap', alignItems: 'center' }}>
        {series.map((s) => {
          const on = !hidden.includes(s.key);
          return (
            <button
              key={s.key}
              type="button"
              aria-pressed={on}
              onClick={() => setHidden(on ? [...hidden, s.key] : hidden.filter((k) => k !== s.key))}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--sp-xs)',
                padding: '2px var(--sp-sm)',
                borderRadius: 'var(--r-chip)',
                border: '1px solid var(--c-border)',
                background: on ? 'var(--c-surface2)' : 'transparent',
                color: on ? 'var(--c-ink)' : 'var(--c-ink3)',
                fontSize: 'var(--fs-admin-colhead)',
                cursor: 'pointer',
              }}
            >
              <span style={{ width: 10, height: 2, background: s.tone, opacity: on ? 1 : 0.3 }} />
              {s.label}
            </button>
          );
        })}
        <span style={{ marginLeft: 'auto', fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
          peak {fmt(max)}
        </span>
      </div>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        role="img"
        aria-label={`${label} ${shown.map((s) => s.label.toLowerCase()).join(', ') || 'nothing selected'}`}
        style={{ width: '100%', height, display: 'block' }}
      >
        {shown.map((s) => (
          <polyline
            key={s.key}
            points={line(s.points)}
            fill="none"
            stroke={s.tone}
            strokeWidth="1.5"
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
          >
            <title>{`${s.label}: ${s.points.map(fmt).join(', ')}`}</title>
          </polyline>
        ))}
      </svg>
    </>
  );
}

export interface FunnelBar {
  key: string;
  label: ReactNode;
  count: number;
  /** Bar length as a share of the first step, 0–100. */
  widthPct: number;
  /** The share continuing from the step before, drawn above the bar; absent on the first step. */
  note?: { text: string; alarm: boolean } | null;
  /** The step the product exists for, drawn in the accent colour. */
  emphasis?: boolean;
}

/** Horizontal bars, one per step, each with its exact count and an optional continuation note. */
export function FunnelBars({ bars, onSelect }: { bars: readonly FunnelBar[]; onSelect: (key: string) => void }) {
  return (
    <div style={{ display: 'grid', gap: 2 }}>
      {bars.map((b) => (
        <Fragment key={b.key}>
          {b.note && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)', paddingLeft: 200, height: 18 }}>
              <Icon
                name="corner-down-right"
                size={12}
                style={{ color: b.note.alarm ? 'var(--c-error)' : 'var(--c-ink3)' }}
              />
              <span
                className="tabular"
                style={{
                  fontSize: 'var(--fs-admin-colhead)',
                  color: b.note.alarm ? 'var(--c-error-text)' : 'var(--c-ink3)',
                }}
              >
                {b.note.text}
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={() => onSelect(b.key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-md)',
              border: 'none',
              background: 'transparent',
              padding: 0,
              cursor: 'pointer',
              font: 'inherit',
              textAlign: 'left',
            }}
          >
            <span
              style={{
                flex: '0 0 192px',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 'var(--fs-admin-ui)',
                color: b.emphasis ? 'var(--c-ink)' : 'var(--c-ink2)',
                fontWeight: b.emphasis ? 'var(--fw-medium)' : 'var(--fw-regular)',
              }}
            >
              {b.label}
            </span>
            <span
              style={{
                flex: 1,
                height: 22,
                background: 'var(--c-sunk)',
                borderRadius: 'var(--r-chip)',
                overflow: 'hidden',
              }}
            >
              <span
                style={{
                  display: 'block',
                  height: '100%',
                  width: `${b.widthPct}%`,
                  background: b.emphasis ? 'var(--c-accent)' : 'var(--c-brand)',
                }}
              />
            </span>
            <span className="tabular" style={{ flex: '0 0 90px', textAlign: 'right', fontSize: 'var(--fs-admin-cell)' }}>
              {fmt(b.count)}
            </span>
          </button>
        </Fragment>
      ))}
    </div>
  );
}
